export type GoalDirection = 'min' | 'max';

export type GoalStatus = 'none' | 'ok' | 'warn' | 'bad' | 'unknown';

export function evaluateGoal(
  actual: number | null,
  target: number,
  direction: GoalDirection | string,
): GoalStatus {
  if (actual == null || !Number.isFinite(actual)) return 'unknown';
  const dir = direction === 'max' ? 'max' : 'min';
  const ok = dir === 'max' ? actual <= target : actual >= target;
  if (ok) return 'ok';
  const margin = dir === 'max' ? target * 1.1 : target * 0.9;
  const warn = dir === 'max' ? actual <= margin : actual >= margin;
  return warn ? 'warn' : 'bad';
}

/** 0–100 progress toward goal (for min: higher actual is better). */
export function goalProgressPercent(
  actual: number | null,
  target: number,
  direction: GoalDirection | string,
): number | null {
  if (actual == null || target <= 0) return null;
  const dir = direction === 'max' ? 'max' : 'min';
  if (dir === 'min') {
    return Math.min(100, Math.round((actual / target) * 100));
  }
  if (actual <= 0) return 100;
  return Math.min(100, Math.round((target / actual) * 100));
}

export function goalStatusLabel(status: GoalStatus): string {
  switch (status) {
    case 'ok':
      return 'Op doel';
    case 'warn':
      return 'Bijna';
    case 'bad':
      return 'Achter op doel';
    case 'unknown':
      return 'Geen data';
    default:
      return '';
  }
}

export function openPipelineValue(
  byOutcome: { outcome: string; total_amount: string }[],
): number {
  let sum = 0;
  for (const row of byOutcome) {
    if (row.outcome === 'open') {
      sum += Number(row.total_amount) || 0;
    }
  }
  return sum;
}

export function formatEuro(amount: number): string {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount);
}

export type AttributionRow = { source: string; c: number };

export function prepareAttributionRows(rows: AttributionRow[], topN = 7): {
  items: { source: string; c: number; pct: number }[];
  other: number;
  total: number;
} {
  const sorted = [...rows].sort((a, b) => b.c - a.c);
  const total = sorted.reduce((s, r) => s + r.c, 0);
  if (total === 0) {
    return { items: [], other: 0, total: 0 };
  }
  const top = sorted.slice(0, topN);
  const otherCount = sorted.slice(topN).reduce((s, r) => s + r.c, 0);
  const items = top.map((r) => ({
    source: r.source,
    c: r.c,
    pct: Math.round((r.c / total) * 1000) / 10,
  }));
  return { items, other: otherCount, total };
}
